import { useEffect, useState } from 'react'
import { Modal } from '../ui'
import { teamApiService, type ClosePreview } from '../../services/teamApiService'
import { ApiError } from '../../services/BaseApiService'
import { formatCents } from '../../utils/billingStanding'
import { formatDay } from '../TeamClosedBanner'
import { colors, fontSize, radius, spacing } from '../../styles/theme'

/**
 * Close the team (Team Admin): what the final bill will be — the current bill, charged in full now — the 30-day
 * window to reopen and what is erased after it, then the team's exact name and, when the admin has MFA, a current
 * code. A template on legal hold blocks it.
 */
export function CloseTeamModal({ open, onClose, onClosed }: { open: boolean; onClose: () => void; onClosed: () => void }) {
  const [preview, setPreview] = useState<ClosePreview | null>(null)
  const [loadError, setLoadError] = useState('')
  const [name, setName] = useState('')
  const [code, setCode] = useState('')
  const [codeSent, setCodeSent] = useState(false)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')

  useEffect(() => {
    if (!open) return
    setPreview(null); setLoadError(''); setName(''); setCode(''); setCodeSent(false); setError('')
    teamApiService.closePreview()
      .then(setPreview)
      .catch(err => setLoadError(err instanceof ApiError ? err.message : 'Could not load the final bill.'))
  }, [open])

  const bill = preview?.finalBill
  const nameMatches = !!preview && name === preview.teamName
  const codeReady = !preview?.mfaRequired || code.trim().length > 0
  const canClose = !!preview && !preview.legalHold && nameMatches && codeReady && !busy

  async function sendCode() {
    setError('')
    try { await teamApiService.sendCloseCode(); setCodeSent(true) }
    catch (err) { setError(err instanceof ApiError ? err.message : 'Could not send the code.') }
  }

  async function confirm() {
    setBusy(true); setError('')
    try {
      await teamApiService.close(name, preview?.mfaRequired ? code.trim() : undefined)
      onClosed()
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Closing the team failed.')
    } finally {
      setBusy(false)
    }
  }

  const input: React.CSSProperties = {
    width: '100%', padding: '8px 10px', border: `1px solid ${colors.border}`, borderRadius: radius.sm,
    fontSize: fontSize.base, color: colors.text, boxSizing: 'border-box',
  }

  return (
    <Modal open={open} onClose={onClose} title="Close team" width={560}
      footer={
        <div style={{ display: 'flex', gap: spacing.sm, justifyContent: 'flex-end' }}>
          <button onClick={onClose} style={{ padding: '8px 16px', background: 'transparent', border: `1px solid ${colors.border}`, borderRadius: radius.sm, cursor: 'pointer', color: colors.text }}>
            Cancel
          </button>
          <button onClick={confirm} disabled={!canClose}
            style={{ padding: '8px 16px', background: colors.error, color: '#fff', border: 'none', borderRadius: radius.sm, cursor: canClose ? 'pointer' : 'not-allowed', fontWeight: 600, opacity: canClose ? 1 : 0.5 }}>
            {busy ? 'Closing…' : bill && (bill.noCharge || bill.disposition !== 'billed') ? 'Close team' : 'Close team and charge final bill'}
          </button>
        </div>
      }>
      {loadError && <div role="alert" style={{ color: colors.error, fontSize: fontSize.sm }}>{loadError}</div>}
      {!preview && !loadError && <div style={{ color: colors.textSecondary, fontSize: fontSize.sm }}>Loading the final bill…</div>}
      {preview && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: spacing.md, fontSize: fontSize.sm, color: colors.text }}>
          {preview.legalHold && (
            <div role="alert" style={{ background: colors.errorLight, color: colors.error, border: `1px solid ${colors.errorBorder}`, borderRadius: radius.sm, padding: spacing.sm }}>
              A template in this team is on legal hold, so the team cannot be closed. Contact support.
            </div>
          )}
          <p style={{ margin: 0 }}>
            Closing stops rendering, API keys, queued jobs, connectors and scheduled promotions at once, and emails every member.
            A Team Admin can reopen the team until <strong>{formatDay(preview.eraseAfter)}</strong> ({preview.graceDays} days).
            After that its templates, documents and keys are permanently erased and every member is removed from the team (their accounts and sign-ins are kept); billing and compliance records are kept.
          </p>

          <div data-testid="final-bill" style={{ border: `1px solid ${colors.border}`, borderRadius: radius.md, padding: spacing.md }}>
            <div style={{ fontWeight: 600, marginBottom: spacing.sm }}>{bill && (bill.noCharge || bill.disposition !== 'billed') ? 'Final bill' : 'Final bill, charged now'}</div>
            {bill && (bill.noCharge || bill.disposition !== 'billed') ? (
              <div style={{ color: colors.textSecondary }}>
                {bill.disposition === 'trial' ? 'No charge: this month is inside your free trial.' : 'No charge: this team is not billed.'}
              </div>
            ) : bill ? (
              <>
                {bill.lines.map((l, i) => (
                  <div key={i} style={{ display: 'flex', justifyContent: 'space-between', gap: spacing.sm }}>
                    <span>{l.label}</span><span>{formatCents(l.amountCents)}</span>
                  </div>
                ))}
                {bill.usageCharges.map((c, i) => (
                  <div key={`u${i}`} style={{ display: 'flex', justifyContent: 'space-between', gap: spacing.sm }}>
                    <span>{c.description}</span><span>{formatCents(c.amountCents)}</span>
                  </div>
                ))}
                {bill.carriedInCents > 0 && (
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}><span>Carried from earlier bills</span><span>{formatCents(bill.carriedInCents)}</span></div>
                )}
                {bill.creditAppliedCents > 0 && (
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}><span>Account credit</span><span>−{formatCents(bill.creditAppliedCents)}</span></div>
                )}
                <div style={{ display: 'flex', justifyContent: 'space-between', fontWeight: 700, borderTop: `1px solid ${colors.border}`, marginTop: spacing.sm, paddingTop: spacing.sm }}>
                  <span>Total</span><span>{formatCents(bill.totalCents)}</span>
                </div>
                <div style={{ color: colors.textMuted, fontSize: fontSize.xs, marginTop: spacing.xs }}>
                  The whole month's minimum and the pages so far. Reopening does not refund it, and the month is not billed again.
                </div>
              </>
            ) : null}
          </div>

          <div>
            <label htmlFor="close-team-name" style={{ display: 'block', fontWeight: 500, marginBottom: spacing.xs }}>
              Type <strong>{preview.teamName}</strong> to confirm
            </label>
            <input id="close-team-name" value={name} onChange={e => setName(e.target.value)} autoComplete="off" style={input} />
          </div>

          {preview.mfaRequired && (
            <div>
              <label htmlFor="close-team-code" style={{ display: 'block', fontWeight: 500, marginBottom: spacing.xs }}>
                {preview.mfaMethod === 'totp' ? 'Code from your authenticator app' : 'Code sent to your email'}
              </label>
              <div style={{ display: 'flex', gap: spacing.sm }}>
                <input id="close-team-code" value={code} onChange={e => setCode(e.target.value)} inputMode="numeric" autoComplete="one-time-code" style={input} />
                {preview.mfaMethod === 'email' && (
                  <button type="button" onClick={sendCode} style={{ whiteSpace: 'nowrap', padding: '8px 12px', background: 'transparent', border: `1px solid ${colors.border}`, borderRadius: radius.sm, cursor: 'pointer', color: colors.text }}>
                    {codeSent ? 'Resend code' : 'Send code'}
                  </button>
                )}
              </div>
            </div>
          )}

          {error && <div role="alert" style={{ color: colors.error }}>{error}</div>}
        </div>
      )}
    </Modal>
  )
}
